import { Button, Dialog, Input } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreerIcal, useEtatIcal, useRevoquerIcal } from '../../api/agenda.js';
import { estApiError } from '../../api/errors.js';
import { identiteCourante } from '../../auth/identite.js';
import { BoutonVerrouille, useLectureSeuleModule } from '../module/LectureSeule.js';
import styles from './AgendaPage.module.css';
export function IcalPanel({ galerieSlug }: { galerieSlug: string }) {
  // Une transition d'identité démonte immédiatement le composant qui porte le secret.
  return (
    <Panneau
      key={`${identiteCourante()?.compteId ?? ''}:${galerieSlug}`}
      galerieSlug={galerieSlug}
    />
  );
}
function Panneau({ galerieSlug }: { galerieSlug: string }) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const etat = useEtatIcal(galerieSlug);
  const creer = useCreerIcal(galerieSlug);
  const revoquer = useRevoquerIcal(galerieSlug);
  // Le lien d'abonnement s'écrit côté serveur : en lecture seule, il se montre verrouillé.
  const { actif: lectureSeule } = useLectureSeuleModule();
  const [url, setUrl] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<'renouveler' | 'revoquer' | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  async function agir(action: 'creer' | 'renouveler' | 'revoquer') {
    setErreur(null);
    try {
      if (action === 'revoquer') {
        await revoquer.mutateAsync();
        setUrl(null);
      } else {
        const resultat = await creer.mutateAsync();
        setUrl(resultat.url);
        creer.reset();
      }
      setConfirmation(null);
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    }
  }
  return (
    <aside className={styles.panneau} data-ui="ical-panel">
      <h2>{t('regie:agenda.ical.titre')}</h2>
      <p>{t('regie:agenda.ical.explication')}</p>
      {etat.isError || erreur ? <p role="alert">{erreur ?? t('commun:erreurReseau')}</p> : null}
      {url ? (
        <>
          <Input aria-label={t('regie:agenda.ical.lien')} value={url} readOnly />
          <p>{t('regie:agenda.ical.uneFois')}</p>
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard
                .writeText(url)
                .catch(() => setErreur(t('regie:agenda.ical.copieErreur')));
            }}
          >
            {t('regie:agenda.ical.copier')}
          </Button>
        </>
      ) : null}
      {etat.data?.actif && etat.data.creeLe ? (
        <p>
          {t('regie:agenda.ical.actifDepuis', {
            date: new Intl.DateTimeFormat(i18n.language).format(new Date(etat.data.creeLe)),
          })}
        </p>
      ) : null}
      <div className={styles.actions}>
        {lectureSeule ? (
          <BoutonVerrouille>
            {t(etat.data?.actif ? 'regie:agenda.ical.renouveler' : 'regie:agenda.ical.creer')}
          </BoutonVerrouille>
        ) : etat.data?.actif ? (
          <>
            <Button variant="secondary" onClick={() => setConfirmation('renouveler')}>
              {t('regie:agenda.ical.renouveler')}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmation('revoquer')}>
              {t('regie:agenda.ical.revoquer')}
            </Button>
          </>
        ) : (
          <Button
            disabled={!etat.data || creer.isPending}
            onClick={() => {
              void agir('creer');
            }}
          >
            {t('regie:agenda.ical.creer')}
          </Button>
        )}
      </div>
      <Dialog
        open={confirmation !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setConfirmation(null);
        }}
        title={t('regie:agenda.ical.confirmation')}
        description={t('regie:agenda.ical.invaliderAncien')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            disabled={creer.isPending || revoquer.isPending}
            onClick={() => {
              if (confirmation) void agir(confirmation);
            }}
          >
            {t(
              `regie:agenda.ical.${confirmation === 'revoquer' ? 'confirmerRevoquer' : 'confirmerRenouveler'}`,
            )}
          </Button>
        }
      />
    </aside>
  );
}
